/** Projects (IPM's research tree) and what they unlock (GDD §3.6). */

import { BASE_VISIBLE_PLANETS, PLANETS, PLANETS_PER_TELESCOPE } from '../content/planets';
import { PROJECTS, projectDef, type Feature } from '../content/projects';
import type { Ingredient } from '../content/resources';
import { cutInputs, roomCut } from './rooms';
import { newSlot, type GameState } from './state';

export function hasProject(state: GameState, id: string): boolean {
  return state.run.projects.includes(id);
}

export function hasFeature(state: GameState, feature: Feature): boolean {
  return state.run.projects.some((id) => projectDef(id).effects.some((e) => e.kind === 'feature' && e.feature === feature));
}

export function telescopes(state: GameState): number {
  return state.run.projects.filter((id) => projectDef(id).effects.some((e) => e.kind === 'telescope')).length;
}

/** Planets you can see (and so buy): four, plus three per Telescope. */
export function visiblePlanets(state: GameState): number {
  return Math.min(PLANETS.length, BASE_VISIBLE_PLANETS + PLANETS_PER_TELESCOPE * telescopes(state));
}

/** Combined ingredient cut from Efficiency projects for a target (they stack additively, as in IPM). */
export function projectEfficiency(state: GameState, target: 'smelter' | 'crafter' | 'colony'): number {
  let cut = 0;
  for (const id of state.run.projects) {
    for (const e of projectDef(id).effects) if (e.kind === 'efficiency' && e.target === target) cut += e.amount;
  }
  return Math.min(0.9, cut);
}

/** A prerequisite is met when any one listed project is done (IPM's "A OR B"). */
export function prerequisitesMet(state: GameState, id: string): boolean {
  const def = projectDef(id);
  return def.requires.length === 0 || def.requires.some((r) => hasProject(state, r));
}

/** Resources after the Laboratory room's cut. */
export function projectCost(state: GameState, id: string): Ingredient[] {
  return cutInputs(projectDef(id).cost, roomCut(state, 'researchCost'));
}

export function canBuyProject(state: GameState, id: string): boolean {
  if (hasProject(state, id) || !prerequisitesMet(state, id)) return false;
  return projectCost(state, id).every((i) => (state.run.hold[i.id] ?? 0) >= i.qty);
}

export function buyProject(state: GameState, id: string): boolean {
  if (!canBuyProject(state, id)) return false;
  for (const i of projectCost(state, id)) state.run.hold[i.id]! -= i.qty;
  state.run.projects.push(id);
  for (const e of projectDef(id).effects) {
    if (e.kind !== 'feature') continue;
    if (e.feature === 'smelter') state.run.smelters.push(newSlot());
    if (e.feature === 'crafter') state.run.crafters.push(newSlot());
  }
  return true;
}

/** Projects worth showing: done, available, or one step from available. */
export function projectStatus(state: GameState, id: string): 'done' | 'ready' | 'short' | 'locked' {
  if (hasProject(state, id)) return 'done';
  if (!prerequisitesMet(state, id)) return 'locked';
  return canBuyProject(state, id) ? 'ready' : 'short';
}

export { PROJECTS };
